/**
 * 游戏状态管理（React hook）
 *
 * 把引擎的 GameState 转成 UI 需要的视图数据，
 * 并处理玩家操作（打牌/立直/和了/鸣牌）。
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  apply,
  createGame,
  type GameConfig,
} from '@mahjong/engine/src/game/engine.js';
import {
  canAnkan,
  canApply,
  canKakan,
  canRiichi,
  canRiichiWithDiscard,
  canRon,
  handToCounts,
  type Action,
} from '@mahjong/engine/src/game/rules.js';
import { countInHand } from '@mahjong/engine/src/game/engine.js';
import { findChiCombinations } from '@mahjong/engine/src/game/ai.js';
import { decidePushFold } from '@mahjong/engine/src/game/pushfold.js';
import { shanten } from '@mahjong/engine/src/shanten.js';
import { tileIndex, type Tile } from '@mahjong/engine/src/tile.js';
import { analyzeWaits } from '@mahjong/engine/src/wait.js';
import type { GameState, Seat } from '@mahjong/engine/src/game/state.js';
import { dealerSeat, nextSeat, seatToWind } from '@mahjong/engine/src/game/state.js';
import { nextRound } from '@mahjong/engine/src/game/flow.js';
import {
  clearTemporaryFuriten,
  emptyTracker,
  recordPass,
  type FuritenTracker,
} from '@mahjong/engine/src/game/furiten.js';

/** 座位名 */
const SEAT_LABEL = ['自家', '下家', '对家', '上家'] as const;
/** 花色中文（按钮文案用）*/
const SUIT_CN: Record<string, string> = { m: '萬', p: '筒', s: '索', z: '字' };
import type { PlayerView, TableCenterInfo } from '../components/Table.js';

/** 固定的伪随机源（可复现） */
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

/** 游戏阶段（UI 视角）*/
export type UIPhase = 'playing' | 'awaiting_claim' | 'over';

export interface UseGameOptions {
  /** 随机种子 */
  seed?: number;
  /** 场风 */
  roundWind?: 'east' | 'south';
}

export function useGame(options: UseGameOptions = {}) {
  const seedRef = useRef(options.seed ?? Date.now() % 100000);
  const randRef = useRef(seededRandom(seedRef.current));

  const [state, setState] = useState<GameState>(() =>
    createGame({
      roundWind: options.roundWind ?? 'east',
      roundNumber: 1,
      random: randRef.current,
    }),
  );

  const [log, setLog] = useState<string[]>([]);
  const [showHints, setShowHints] = useState(false);

  const pushLog = useCallback((msg: string) => {
    setLog((l) => [msg, ...l].slice(0, 50));
  }, []);

  /** 自家座位（本 UI 自家固定为座位 0）*/
  const selfSeat: Seat = 0;

  // ════════════════════════════════════════════════════════════════
  // 视图数据转换
  // ════════════════════════════════════════════════════════════════

  const players: PlayerView[] = useMemo(() => {
    return state.players.map((p) => {
      const meldTiles = p.melds.reduce((s, m) => s + (m.kind === 'kantsu' ? 4 : 3), 0);
      const isSelf = p.seat === selfSeat;

      // 自家：手牌全部显示（引擎已按 34 索引排序）
      // ⚠️ 不要把"刚摸的牌"靠位置拆出来 —— doDraw 排序后第 14 张可能是任意牌，
      //    会导致手牌显示乱序（踩过：vision 报告手牌「没按标准顺序排列」）。
      //    正确做法：整手牌一起显示，摸到的牌用 id 追踪并高亮。
      const handIdx = isSelf ? p.hand.map(tileIndex) : [];
      const handRed = isSelf ? p.hand.map((t) => t.red) : [];
      // 摸到的牌：由 state 跟踪的 id（而非位置）
      const drawnIdx =
        isSelf && state.drawnTileId != null
          ? p.hand.findIndex((x) => x.id === state.drawnTileId)
          : -1;
      const drawnTile = drawnIdx >= 0 ? tileIndex(p.hand[drawnIdx]) : null;

      return {
        seat: p.seat,
        hand: handIdx,
        handRed,
        drawnTile,
        drawnIndex: drawnIdx,
        melds: p.melds.map((m) => ({
          kind: m.kind,
          start: m.start,
          open: m.open,
          // ⚠️ 必须用引擎记录的被鸣牌位置（写死 0 会导致吃牌时横置的永远是第 1 张）
          calledIndex: m.calledIndex ?? 0,
        })),
        discards: p.discards.map(tileIndex),
        discardsRed: p.discards.map((t) => t.red),
        riichi: p.riichi,
        // ⚠️ 用引擎记录的宣言牌下标（不能用 length-1，那会随每张牌移动）
        riichiDiscardIndex: p.riichiDiscardIndex,
        score: p.score,
        isDealer: p.seat === dealerSeat(state.round),
        wind: seatToWind(p.seat, state.round),
        isTenpai: isSelf ? shanten(handToCounts(p.hand), p.melds.length) <= 0 : undefined,
      };
    });
  }, [state, selfSeat]);

  const center: TableCenterInfo = useMemo(
    () => ({
      roundWind: state.round.roundWind,
      roundNumber: state.round.roundNumber,
      honba: state.round.honba,
      riichiSticks: state.round.riichiSticks,
      tilesRemaining: state.wall.live.length,
      doraIndicators: state.wall.doraIndicators.map(tileIndex),
      activeSeat: state.activeSeat,
    }),
    [state],
  );

  // ════════════════════════════════════════════════════════════════
  // 玩家操作
  // ════════════════════════════════════════════════════════════════

  /** 自家是否该行动 */
  const isSelfTurn = state.phase === 'discard' && state.activeSeat === selfSeat;

  /** 打牌 */
  const discard = useCallback(
    (tileIdx: number, riichi = false) => {
      const player = state.players[selfSeat];
      const tile = player.hand.find((t) => tileIndex(t) === tileIdx);
      if (!tile) {
        pushLog(`⚠️ 手牌中没有这张牌`);
        return;
      }
      const v = canApply(state, { type: 'discard', tile, riichi }, selfSeat);
      if (!v.legal) {
        pushLog(`⚠️ ${v.reason}`);
        return;
      }
      setState(apply(state, { type: 'discard', tile, riichi }));
      pushLog(`打 ${tile.num}${tile.suit}${riichi ? '（立直）' : ''}`);
    },
    [state, pushLog],
  );

  /** 自摸和了 */
  const tsumo = useCallback(() => {
    const v = canApply(state, { type: 'tsumo' });
    if (!v.legal) {
      pushLog(`⚠️ ${v.reason}`);
      return;
    }
    setState(apply(state, { type: 'tsumo' }));
    pushLog('自摸和了！');
  }, [state, pushLog]);

  /** 当前能否立直 */
  const canRiichiNow = useMemo(
    () => isSelfTurn && canRiichi(state).legal,
    [isSelfTurn, state],
  );

  /** 宣言立直（自动选择能保持听牌的打牌）*/
  const riichi = useCallback(() => {
    if (!isSelfTurn) return;
    const player = state.players[selfSeat];
    // 找能立直的打牌（优先 AI 推荐的）
    const options = [...player.hand].filter(
      (t) => canRiichiWithDiscard(state, t).legal,
    );
    if (options.length === 0) {
      pushLog('⚠️ 当前无法立直（打牌后未听牌）');
      return;
    }
    // 选牌效最好的
    let best = options[0];
    try {
      const decision = decidePushFold(state, selfSeat);
      const match = options.find((t) => tileIndex(t) === tileIndex(decision.tile));
      if (match) best = match;
    } catch {
      // 用第一个可选项
    }
    setState(apply(state, { type: 'discard', tile: best, riichi: true }));
    pushLog(`立直！打 ${best.num}${best.suit}`);
  }, [state, isSelfTurn, selfSeat, pushLog]);

  // ════════════════════════════════════════════════════════════════
  // 鸣牌 / 荣和响应（玩家决策）
  // ════════════════════════════════════════════════════════════════

  /** 当前是否等玩家响应（他家刚打牌 + 自家有选项）*/
  const pendingResponse = useMemo(() => {
    if (state.phase !== 'claim') return null;
    const discard = state.lastDiscard;
    if (!discard || discard.bySeat === selfSeat) return null;

    const tile = tileIndex(discard.tile);
    const player = state.players[selfSeat];
    const options: Array<{
      kind: 'chi' | 'pon' | 'kan' | 'ron';
      label: string;
      tiles?: number[];
      display?: number[];
    }> = [];

    // 荣和（含振听检查）
    const ronV = canRon(state, selfSeat);
    if (ronV.legal) {
      options.push({ kind: 'ron', label: '荣和' });
    }

    // 副露（立直后不能鸣牌）
    if (!player.riichi) {
      // 碰
      const cnt = countInHand(player.hand, tile);
      if (cnt >= 2) {
        const v = canApply(state, { type: 'pon' }, selfSeat);
        if (v.legal) options.push({ kind: 'pon', label: '碰', display: [tile, tile] });
      }

      // 明杠（手中有 3 张）
      if (cnt >= 3) {
        const v = canApply(
          state,
          { type: 'kan', kind: 'minkan', tile: discard.tile, fromSeat: discard.bySeat },
          selfSeat,
        );
        if (v.legal) options.push({ kind: 'kan', label: '杠' });
      }

      // 吃（只能吃上家 = 自家上家是 (selfSeat+3)%4）
      const kamicha = (selfSeat + 3) % 4;
      if (discard.bySeat === kamicha && tile < 27) {
        for (const combo of findChiCombinations(player.hand, tile)) {
          const v = canApply(state, { type: 'chi', tiles: combo }, selfSeat);
          if (v.legal) {
            options.push({
              kind: 'chi',
              label: '吃',
              tiles: combo.map(tileIndex),
              display: [...combo.map(tileIndex), tile].sort((a, b) => a - b),
            });
          }
        }
      }
    }

    if (options.length === 0) return null;
    return { discardTile: tile, discarderName: SEAT_LABEL[discard.bySeat], options };
  }, [state, selfSeat]);

  /** 执行响应 */
  const respond = useCallback(
    (opt: { kind: string; tiles?: number[] }) => {
      const discard = state.lastDiscard;
      if (!discard) return;

      if (opt.kind === 'ron') {
        if (!canRon(state, selfSeat).legal) {
          pushLog('⚠️ 振听中，不能荣和');
          return;
        }
        setState(apply({ ...state, activeSeat: selfSeat }, { type: 'ron' }));
        pushLog('荣和！');
        return;
      }

      const player = state.players[selfSeat];
      let action: Action | null = null;

      if (opt.kind === 'pon') {
        action = { type: 'pon' };
      } else if (opt.kind === 'kan') {
        action = {
          type: 'kan',
          kind: 'minkan',
          tile: discard.tile,
          fromSeat: discard.bySeat,
        };
      } else if (opt.kind === 'chi' && opt.tiles) {
        // tiles 是牌索引 → 找回实际 Tile
        const picked: Tile[] = [];
        const used = new Set<number>();
        for (const idx of opt.tiles) {
          const t = player.hand.find((x) => tileIndex(x) === idx && !used.has(x.id));
          if (t) {
            used.add(t.id);
            picked.push(t);
          }
        }
        if (picked.length === 2) action = { type: 'chi', tiles: [picked[0], picked[1]] };
      }

      if (!action) {
        pushLog('⚠️ 无法执行该操作');
        return;
      }

      const v = canApply(state, action, selfSeat);
      if (!v.legal) {
        pushLog(`⚠️ ${v.reason}`);
        return;
      }

      const label = { chi: '吃', pon: '碰', kan: '杠' }[opt.kind as 'chi'];
      pushLog(`自家 ${label}`);
      const after = apply({ ...state, activeSeat: selfSeat }, action);
      setState({
        ...after,
        furitenTracker: clearTemporaryFuriten(after.furitenTracker, selfSeat),
      });
    },
    [state, selfSeat, pushLog],
  );

  /** 放弃响应 */
  const passResponse = useCallback(() => {
    const discard = state.lastDiscard;
    if (!discard) return;
    // 走 driver 的默认路径（下一家摸牌）
    const next = nextSeat(discard.bySeat);
    let tracker = state.furitenTracker ?? emptyTracker();
    // 记录振听（若本来能荣和却放弃）
    const player = state.players[selfSeat];
    tracker = recordPass(tracker, selfSeat, discard.tile, player);
    setState(
      apply(
        {
          ...state,
          activeSeat: next,
          lastDiscard: null,
          furitenTracker: tracker,
        },
        { type: 'pass' },
      ),
    );
    pushLog('放弃');
  }, [state, selfSeat, pushLog]);

  /** 推荐的打牌（提示用）*/
  const hints = useMemo(() => {
    if (!showHints || !isSelfTurn) return [];
    try {
      const decision = decidePushFold(state, selfSeat);
      return [tileIndex(decision.tile)];
    } catch {
      return [];
    }
  }, [showHints, isSelfTurn, state]);

  /** 当前建议的详情（提示文案）*/
  const hintText = useMemo(() => {
    if (!showHints || !isSelfTurn) return null;
    try {
      const d = decidePushFold(state, selfSeat);
      const player = state.players[selfSeat];
      const counts = handToCounts(player.hand);
      const s = shanten(counts, player.melds.length);
      const waits = analyzeWaits(counts, player.melds);
      const waitStr = waits.waits.map((w) => w.name).join(' ');
      // 牌的显示名（万/筒/索/字）
      const suitName = (t: { num: number; suit: string }) => {
        const n = '一二三四五六七八九'[t.num - 1] ?? String(t.num);
        return t.suit === 'z' ? '东南西北白发中'[t.num - 1] : `${n}${t.suit === 'm' ? '万' : t.suit === 'p' ? '筒' : '索'}`;
      };
      return {
        advice: d.reason,
        shanten: s,
        waits: waitStr,
        tileName: suitName(d.tile),
        recommend: `打 ${suitName(d.tile)}`,
      };
    } catch {
      return null;
    }
  }, [showHints, isSelfTurn, state]);

  // ════════════════════════════════════════════════════════════════
  // 杠（暗杠 / 加杠）—— 自家出牌阶段可杠时提示
  // ════════════════════════════════════════════════════════════════

  /** 当前可执行的杠（加杠优先，其次暗杠）*/
  const kanOptions = useMemo(() => {
    const opts: Array<{ kind: 'ankan' | 'kakan'; tile: Tile; label: string }> = [];
    if (!isSelfTurn) return opts;
    const player = state.players[selfSeat];
    if (player.riichi) return opts; // 立直后（简化）不允许杠
    const seen = new Set<number>();
    for (const t of player.hand) {
      const idx = tileIndex(t);
      if (seen.has(idx)) continue;
      seen.add(idx);
      const cn = SUIT_CN[t.suit] ?? '';
      if (canKakan(state, t).legal) {
        opts.push({ kind: 'kakan', tile: t, label: `加杠 ${t.num}${cn}` });
      } else if (canAnkan(state, t).legal) {
        opts.push({ kind: 'ankan', tile: t, label: `暗杠 ${t.num}${cn}` });
      }
    }
    return opts;
  }, [state, selfSeat, isSelfTurn]);

  /** 执行杠 */
  const kan = useCallback(
    (opt: { kind: 'ankan' | 'kakan'; tile: Tile }) => {
      const action: Action = { type: 'kan', kind: opt.kind, tile: opt.tile };
      const v = canApply(state, action, selfSeat);
      if (!v.legal) {
        pushLog(`⚠️ ${v.reason}`);
        return;
      }
      setState(apply(state, action));
      pushLog(opt.kind === 'kakan' ? '加杠！' : '暗杠！');
    },
    [state, selfSeat, pushLog],
  );

  // ════════════════════════════════════════════════════════════════
  // 重置 / 新局
  // ════════════════════════════════════════════════════════════════

  /**
   * 开新局
   *
   * @param advance 是否推进到下一局（连庄/轮庄）；
   *                false = 重置回东1局
   */
  const newGame = useCallback(
    (advance = false) => {
      seedRef.current = Date.now() % 100000;
      randRef.current = seededRandom(seedRef.current);

      if (advance && state.result) {
        // 用引擎的 nextRound 计算下一局（含连庄判断 / 庄家轮换）
        const nextRoundInfo = nextRound(state, state.result);
        // 换牌山重新开局（保留局数信息）
        const fresh = createGame({
          roundWind: nextRoundInfo.roundWind,
          roundNumber: nextRoundInfo.roundNumber,
          honba: nextRoundInfo.honba,
          riichiSticks: nextRoundInfo.riichiSticks,
          scores: state.players.map((p) => p.score),
          random: randRef.current,
        });
        setState(fresh);
        pushLog(`=== ${nextRoundInfo.roundWind === 'east' ? '东' : '南'}${nextRoundInfo.roundNumber}局 ${nextRoundInfo.honba > 0 ? nextRoundInfo.honba + '本场' : ''} ===`);
      } else {
        setState(
          createGame({
            roundWind: options.roundWind ?? 'east',
            roundNumber: 1,
            random: randRef.current,
          }),
        );
        setLog([]);
      }
    },
    [options.roundWind, state, pushLog],
  );

  return {
    state,
    setState,
    pushLog,
    players,
    center,
    selfSeat,
    log,
    isSelfTurn,
    hints,
    hintText,
    showHints,
    setShowHints,
    discard,
    tsumo,
    riichi,
    pendingResponse,
    respond,
    passResponse,
    canRiichiNow,
    kanOptions,
    kan,
    newGame,
  };
}
